import { COMMANDS } from '../../../utils/commonProperties';
import * as THREE from 'three';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import parseColor from '../../../utils/parseColor';
import { setCommandMessage, resetOperation, setCommandType, setOperationData  } from '../../../redux/operationSlice';
import { clearSelection } from '../../../redux/selectionSlice';
import { useComponentWiring } from '../../../modules/wiring';
import { updatePanelTitles } from '../../../modules/wiring';
import { createMachineSymbol } from '../../../factory/symbols/createMachines';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import useSnapPoints from '../../../shared/snap/useSnapPoints';
import { addAction } from '../../../redux/operationHistorySlice';

// 🆕 simpleSupply import'u
import { addLoadToComponent } from '../../../redux/simpleSupplySlice';
import { FeederFactory } from '../../../factories/FeederFactory';
import { MachineCommand } from '../../../modules/undoRedo';
import { findObjectByUUID } from '../../../utils/findObjectByUUID';

// İnterkom hattı yeşil (resimdeki gibi)
const INTERKOM_GREEN = 0x33cc33;

// Makine ve İnterkom aynı çizim/undo/redo/load pipeline'ını paylaşır; tek fark
// komut + renk + cihaz listesi. Variant ile parametrize edilir (kod tekrarı yok).
const MACHINE_VARIANT = {
    command: COMMANDS.DRAW_MACHINE,
    itemTypeName: 'machine',
    startMessage: 'Makineyi bağlamak için hatta tıklayın',
    endMessage: 'Makine yerleştirildi (Ctrl+Z ile geri alınabilir)',
    defaultType: 'Çamaşır Makinası',
    defaultPower: 2500,
    itemTypes: [
        { name: 'Çamaşır Makinası', type: 'Çamaşır Makinası', power: 2500 },
        { name: 'Kurutma Makinesi', type: 'Kurutma Makinesi', power: 2500 },
        { name: 'Bulaşık Makinesi', type: 'Bulaşık Makinesi', power: 2500 },
        { name: 'Fırın', type: 'Fırın', power: 2000 },
        { name: 'Kombi', type: 'Kombi', power: 300 },
        { name: 'Klima', type: 'Klima', power: 1500 },
        { name: 'Fan', type: 'Fan', power: 500 },
        { name: 'Motor', type: 'Motor', power: 1000 },
        { name: 'Hidrofor', type: 'Hidrofor', power: 750 }
    ],
    wireColor: (settings) => parseColor(settings?.machine?.wireColor ?? 0xffff00).colorNumber,
    symbolColor: (settings, isPreview) => parseColor(isPreview
        ? (settings?.machine?.previewColor ?? settings?.machine?.color ?? 0xffff00)
        : (settings?.machine?.color ?? 0xffff00)).colorNumber,
    chosenColor: (settings) => parseColor(settings?.machine?.color ?? settings?.machine?.wireColor ?? 0x2233ff).colorNumber,
};

const INTERKOM_VARIANT = {
    command: COMMANDS.DRAW_INTERKOM,
    itemTypeName: 'interkom',
    startMessage: 'İnterkomu bağlamak için hatta tıklayın',
    endMessage: 'İnterkom yerleştirildi (Ctrl+Z ile geri alınabilir)',
    defaultType: 'İnterkom',
    defaultPower: 200,
    itemTypes: [{ name: 'İnterkom', type: 'İnterkom', power: 200 }],
    wireColor: () => INTERKOM_GREEN,
    symbolColor: () => INTERKOM_GREEN,
    chosenColor: () => INTERKOM_GREEN,
};

const useDrawMachine = (scene, camera, renderer, undoRedoManagerRef) => {
    const dispatch = useDispatch();
    const { commandType } = useSelector((state) => state.operation);
    // Aktif komuta göre variant seç (makine ↔ interkom)
    const variant = commandType === COMMANDS.DRAW_INTERKOM ? INTERKOM_VARIANT : MACHINE_VARIANT;
    const settings = useSelector(state => state.settings);
    const { snapPoints } = useSnapPoints(scene);

    const dynamicSnapPoints = useRef([]);
    const previewMachineRef = useRef(null);
    const verticesRef = useRef(null);
    const wiringActiveRef = useRef(false);
    const sceneRef = useRef(null); // ✅ scene'i güvenli saklamak için
    const operation = useSelector((state) => state.operation);

    // 🆕 Machine/İnterkom Configuration (variant ile)
    const machineConfig = {
        commandType: variant.command,
        itemTypeName: variant.itemTypeName,
        wireColor: variant.wireColor(settings),
        allowSpaceKey: false, // ✅ Machine'de Space tuşu ile ekleme yapılmaz, sadece Enter kullanılır
        skipItemExecution: true, // ✅ addItem()'da execute atlanır, handleWireEnd'de yapılır (çift eklemeyi önler)
        // itemOffset KULLANMA - createMachineSymbol zaten kendi içinde 25 birimlik offset uyguluyor
        itemTypes: variant.itemTypes,
        createSymbol: (position, direction, angle, isPreview, itemType, extraData = {}) => {
            return createMachineSymbol(
                null,
                position,
                direction,
                variant.symbolColor(settings, isPreview),
                isPreview,
                itemType?.type || variant.defaultType,
                itemType?.power || variant.defaultPower,
                angle
            );
        },
        // ❌ createCommand KALDIRILDI - Machine için command sadece handleWireEnd içinde oluşturulur
        createCommand: null,
        // ❌ Item-add feeder/component KULLANILMIYOR (gerçekleri handleWireEnd'de). null verilmezse
        // addItem, itemTypeName'i load type olarak FeederFactory'ye geçirir → 'interkom' geçersiz
        // load type hatası. null ile bu gereksiz çağrı atlanır (makine için de zararsız — kullanılmıyordu).
        createComponentData: null,
        createFeederData: null,
        onKeyDown: null
    };



    // ✅ RESTORE VERTICES useEffect (undo-redo branch'indeki useMachineWiring'den)
    useEffect(() => {
        const hasRestoreVertices = operation.data?.restoreVertices && Array.isArray(operation.data.restoreVertices) && operation.data.restoreVertices.length > 0;

      
    }, [commandType, operation.data?._restoreId, scene, camera, renderer]);

    useEffect(() => {
        const isActive = commandType === COMMANDS.DRAW_MACHINE || commandType === COMMANDS.DRAW_INTERKOM;
        if (!isActive || !scene || !camera || !renderer) {
            if (isActive) {
                console.warn('[useMachines] Command active but scene/camera/renderer missing!', { scene: !!scene, camera: !!camera, renderer: !!renderer });
            }
            return;
        }

        dispatch(setCommandMessage(variant.startMessage));
        // eslint-disable-next-line react-hooks/exhaustive-deps

        sceneRef.current = scene; // 🔁 güvenli erişim için sakla

        // Sadece panel çıkışlarını snap noktası olarak al
        dynamicSnapPoints.current = [];
        scene.traverse(obj => {
            if (obj.userData?.connectionPoints) {
                obj.userData.connectionPoints.forEach(p => {
                    if (p.type === 'panelCon_out') {
                        const worldPos = p.position.clone().applyMatrix4(obj.matrixWorld);
                        dynamicSnapPoints.current.push({
                            no: obj.uuid,
                            position: worldPos,
                            type: 'panelConnection'
                        });
                    }
                });
            }
        });

        wiringActiveRef.current = true;

        // ✅ Cleanup: commandType değiştiğinde wiring'i devre dışı bırak
        return () => {
            wiringActiveRef.current = false;
        };
    }, [commandType, scene, camera, renderer]);

    // Machine placement handler (called when Space is pressed in useComponentWiring)
    const handleMachinePlace = (position, direction) => {
        // Machine'de Space ile ekleme yok, sadece Enter ile ekleniyor
        // Bu yüzden bu callback kullanılmayacak ama API uyumluluğu için boş bırakıyoruz
        return null;
    };

    // Machine removal handler
    const handleMachineRemove = (machine) => {
        if (!machine) return;
        sceneRef.current.remove(machine);
        machine.traverse(child => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) child.material.dispose();
        });
    };

    // Wire end handler - called when Enter is pressed
    const handleWireEnd = (vertices, machines, source, machine, tempLineUUID, wireMarkers) => {
        // machines parametresi base.itemsRef.current (array)
        // machine parametresi base.itemsRef.current[0] (ilk/tek makine)

        // 🆕 REDO kontrolü: REDO sırasında yeni transaction başlatma
        // REDO'da command zaten var, sadece notifyRedoComplete çağrılacak
        const isRedoMode = operation?.data?.isRedo;
        if (isRedoMode) {
             return;
        }

        if (!source || !source.p) {
            return;
        }

        // Machine enter'da ekleniyor, machines array'inde olmalı
        if (!machine && (!machines || machines.length === 0)) {
            console.warn('No machine found in handleWireEnd');
            return;
        }

        // İlk makineyi al (machines array'inden veya direkt machine parametresinden)
        const machineData = machine || (machines && machines.length > 0 ? machines[0] : null);

        if (!machineData) {
            console.warn('No machine data found');
            return;
        }

        const power = machineData.power;
        const machineType = machineData.type;
                let branchID;
                let  totalLength = 0;
                for (let i = 0; i < vertices.length - 1; i++) {
                    totalLength += vertices[i].distanceTo(vertices[i + 1]);
                }

                const foundPanelOutSnap = (source.type === "panelConnection")
                    ? findObjectByUUID(scene, source.p.UUID)
                    : findObjectByUUID(scene, source.no);

                const panel = source?.userData?.sourceObject ?? foundPanelOutSnap;
                dispatch(addAction({
                    type: 'polylineWireCreate',
                    objectUUID: branchID,
                    after: {
                        vertices: vertices.map(v => v.toArray()),
                        color: variant.chosenColor(settings)
                    }
                }));

                // ✅ Branch ve Wire ID üret
                branchID = generateUniqueId("branchMachine");
                const wireID = generateUniqueId("wireMachine");

                // ❌ KALDIRILDI: Wire ve branchGroup sahneye ekleme - MachineCommand.execute() yapacak
                // const branchGroup = new THREE.Group();
                // branchGroup.userData = {...};
                // const line = new THREE.Line(geometry, material);
                // sceneRef.current.add(branchGroup);
                // dispatch(addWire(...)); - MachineCommand.execute() içinde yapılacak


                // 🆕 feederId'yi önce tanımla
                const machineFeederIdx = generateUniqueId("feederMachine");

                // 🆕 YENİ SİSTEM: addLoadToComponent kullan
                // ✅ FIX: ComponentFactory kullanarak doğru veri yapısını oluştur
                const machineComponentId = generateUniqueId('load');
                const newMachineLoadData = {
                    id: machineComponentId,
                    kind: 'LOAD',
                    name: machineType,
                    position: {
                        x: machineData.position.x,
                        y: machineData.position.y,
                        z: machineData.position.z || 0
                    },
                    rotation: {
                        x: 0,
                        y: 0,
                        z: machineData.angle || 0
                    },
                    electrical: {
                        installedPower: power,
                        demandPower: power, // ✅ Demand power da ekle
                        current: power / 220, // 1 faz için akım hesabı
                        phaseCount: 1,
                    },
                    parentComponentId: panel.userData?.id || panel.id,
                    metadata: {
                        wireId: wireID,
                        branchId: branchID,
                        branchType: 'machine', // ✅ branchType metadata'da kalmalı
                        loadType: 'machine', // ✅ loadType da ekle (transform fonksiyonları için)
                        machineType: machineType,
                        machine: {
                            ...machineData,
                            position: { x: machineData.position.x, y: machineData.position.y, z: machineData.position.z || 0 }, // ✅ Serialize Vector3
                            direction: machineData.direction ? { x: machineData.direction.x, y: machineData.direction.y, z: machineData.direction.z || 0 } : null // ✅ Serialize Vector3
                        },
                        length: totalLength.toFixed(2)
                    }
                };

                // Wire/sembol rengi variant'tan (makine: ayarlardan, interkom: yeşil)
                const parsedChosen = parseColor(variant.chosenColor(settings));

                // FeederFactory ile doğru cable ve electrical bilgilerle machine feeder oluştur
                const machineFeeder = FeederFactory.createFeederForLoad(
                    'machine',
                    power,
                    panel.userData?.id || panel.id,
                    machineComponentId, // ✅ FIX: newMachineLoadData.id yerine machineComponentId kullan
                    {
                        id: machineFeederIdx,
                        vertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
                        cable: {
                            length: Math.round(totalLength / 10) // mm -> cm
                        },
                        electrical: {
                            phaseCount: 1,
                            voltage: 220
                        },
                        metadata: {
                            branchType: 'machine',
                            length: totalLength.toFixed(2),
                            machineType: machineType,
                            wireId: wireID,
                            color: parsedChosen.colorNumber
                        }
                    }
                );

                const machineFeederData = machineFeeder;

                // ✅ YENİ: addLoadToComponent KALDIRILDI - cmd.execute() yapacak
                // BaseWiringCommand._addComponent artık addLoadToComponent kullanıyor
                // Bu sayede normal oluşturma ve REDO aynı yolu kullanır

                // 🔵 Global Undo/Redo Transaction
                if (undoRedoManagerRef?.current) {
                    
                    const tx = undoRedoManagerRef.current.beginTransaction('Add Machine');

                    // Machine symbol için gerekli data'yı hazırla
                    const machineSymbolData = {
                        position: { x: machineData.position.x, y: machineData.position.y, z: machineData.position.z || 0 },
                        flipDirection: machineData.direction
                            ? { x: machineData.direction.x, y: machineData.direction.y, z: machineData.direction.z || 0 }
                            : { x: 1, y: 0, z: 0 },
                        machineType: machineType,
                        power: power,
                        color: parsedChosen ? parsedChosen.colorNumber : undefined,
                        angle: machineData.angle
                    };

                    // Snapshot the minimal source info so restore doesn't rely on live operation state
                    const sourceSnapshot = source ? {
                        type: source.type,
                        p: source.p ? { ...source.p } : null,
                        no: source.no,
                        UUID: source.p?.UUID || source.no,
                        panelID: source.userData?.sourceObject?.userData?.id || source.userData?.sourceObject?.id || null,
                        // ✅ Serialize Vector3 position
                        position: source.position ? {
                            x: source.position.x,
                            y: source.position.y,
                            z: source.position.z || 0
                        } : null
                    } : null;

                    const cmd = new MachineCommand('ADD', newMachineLoadData, machineFeederData, {
                        scene: sceneRef.current,
                        dispatch,
                        wireId: wireID,
                        machineSymbolData,
                        restoreVertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
                        // include the snapshot on the command for future-proofing (optional)
                        restoreSource: sourceSnapshot,
                        // Robust restore: set command type first, then dispatch operation data on next tick
                        restoreCallback: (restoreVertices, _fromSnapshot = sourceSnapshot, isRedoContext = false, machineData = null) => {

                            dispatch(setCommandType(variant.command));

                            // small delay to allow the consumers of commandType to enable wiring
                            setTimeout(() => {
                                // ✅ REDO ise: İlk vertex yükle, geri kalanlar history'de
                                // ✅ UNDO ise: Tüm vertices yükle
                                const operationData = {
                                    initialPoint: restoreVertices && restoreVertices[0] ? restoreVertices[0] : null,
                                    source: _fromSnapshot,
                                    _restoreId: Date.now() + Math.random() // ✅ Benzersiz ID
                                };

                                // ✅ Machine pozisyon ve açı bilgilerini ekle (redo için)
                                if (machineData) {
                                    operationData.machineData = {
                                        position: machineData.machinePosition || machineData.position,
                                        angle: machineData.angle ?? machineData.rotation ?? machineData.rotateValue ?? 0,
                                        direction: machineData.dir || machineData.flipDirection,
                                        machineType: machineData.machineType,
                                        power: machineData.power
                                    };
                                }

                                if (isRedoContext) {
                                    // REDO: Sadece ilk vertex, geri kalanlar vertexHistory'de
                                    operationData.restoreVertices = [restoreVertices[0]]; // Sadece ilk vertex
                                    operationData.vertexHistory = restoreVertices.slice(1); // Geri kalanlar
                                    operationData.isRedo = true;
                                    // 🆕 onRedoComplete callback'ini aktar (chain advancement için)
                                    operationData.onRedoComplete = machineData?.onRedoComplete;
                                 } else {
                                    // UNDO: Tüm vertices yükle
                                    operationData.restoreVertices = restoreVertices;
                                    operationData.isRedo = false;
                                 }

                                dispatch(setOperationData(operationData));
                            }, 16);
                        }
                    });
                    

                    // ✅ EXECUTE - Sahne objelerini ekle (Redux zaten eklendi)
                    try {
                        
                        cmd.execute(sceneRef.current, dispatch);
                    } catch (error) {
                        console.error('❌ MachineCommand.execute() failed:', error);
                    }

                    undoRedoManagerRef.current.addCommand(cmd);
                    undoRedoManagerRef.current.commitTransaction(tx);
                    

                    // Ensure panel titles are refreshed after MachineCommand.execute
                    try {
                        if (panel && panel.userData && (panel.userData.id || panel.id)) {
                            const panelId = panel.userData?.id || panel.id;
                            
                            updatePanelTitles(sceneRef.current, panelId);
                        }
                    } catch (err) {
                        console.warn('⚠️ Machine: updatePanelTitles failed', err);
                    }
                } else {
                    console.warn('⚠️ undoRedoManagerRef?.current is undefined - machine eklendi ama undo-redo yok');

                    // ✅ Fallback: undoRedoManager yoksa doğrudan Redux'a ekle
                    dispatch(addLoadToComponent({
                        parentComponentId: panel.userData?.id || panel.id,
                        loadData: newMachineLoadData,
                        feederData: machineFeederData,
                    }));
                }

        // ✅ TEK SEFERLIK: İşlemi bitir
        dispatch(setCommandMessage(variant.endMessage));

        // ✅ Socket pattern: Ensure operation ends properly
        try {
            dispatch(resetOperation());
            dispatch(clearSelection());
            wiringActiveRef.current = false;
        } catch (e) {
            console.warn('Could not reset operation after machine add', e);
        }

        return { wireId: null }; // Return value for compatibility
    };

    // Item add handler (called when Space is pressed)
    const handleItemAdd = (item) => {
        // Machine'de Space ile ekleme yapılmaz, sadece Enter kullanılır
    };

    // Use the component wiring hook
    useComponentWiring(
        scene,
        camera,
        renderer,
        [...snapPoints, ...dynamicSnapPoints.current],
        {
            ...machineConfig,
            enabled: () => wiringActiveRef.current,
            onWireEnd: handleWireEnd,
            onSocketPlace: handleMachinePlace, // Not used for machines
            onItemAdd: handleItemAdd, // Not used for machines (Enter instead of Space)
            onSocketRemove: handleMachineRemove,
            onPreviewUpdate: () => {},
            onWireCancel: () => {
                wiringActiveRef.current = false;
                dispatch(resetOperation());
            }
        }
    );
};

export default useDrawMachine;

